/* ============================================
   KARA3 - Theme JavaScript
   ============================================ */

(function() {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shopRoot = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  /* --- Header: scrolled state (one class write per state change) --- */
  const header = document.querySelector('[data-header]');
  if (header && header.classList.contains('header--sticky')) {
    let scrolled = null;
    let headerFrame = 0;
    const updateHeader = () => {
      headerFrame = 0;
      const next = window.scrollY > 24;
      if (next === scrolled) return;
      scrolled = next;
      header.classList.toggle('header--scrolled', next);
    };
    window.addEventListener('scroll', () => { if (!headerFrame) headerFrame = requestAnimationFrame(updateHeader); }, { passive: true });
    updateHeader();
  }

  /* --- Shared overlay helpers --- */
  const overlay = document.getElementById('overlay');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activePanel = null;

  function setPanelOpen(panel, open) {
    if (!panel) return;
    panel.classList.toggle('active', open);
    panel.setAttribute('aria-hidden', String(!open));
    panel.toggleAttribute('inert', !open);
  }

  function trapFocus(event) {
    if (!activePanel || event.key !== 'Tab') return;
    const items = Array.from(activePanel.panel.querySelectorAll(focusableSelector)).filter(item => item.getClientRects().length);
    if (!items.length) { event.preventDefault(); activePanel.panel.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !activePanel.panel.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function openPanel(panel, trigger, focusTarget) {
    if (!panel) return;
    if (activePanel && activePanel.panel !== panel) closePanel(false);
    closeMobileMenu(false);
    activePanel = { panel, trigger: trigger || document.activeElement };
    setPanelOpen(panel, true);
    if (overlay && panel.id === 'cartDrawer') overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    const target = focusTarget || panel.querySelector(focusableSelector) || panel;
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  function closePanel(restoreFocus = true) {
    if (!activePanel) return;
    const { panel, trigger } = activePanel;
    activePanel = null;
    setPanelOpen(panel, false);
    if (overlay) overlay.classList.remove('active');
    document.body.style.overflow = '';
    if (restoreFocus && trigger?.isConnected && trigger.getClientRects().length) trigger.focus({ preventScroll: true });
  }

  /* --- Mobile Menu --- */
  const mobileToggle = document.getElementById('mobileMenuToggle');
  const mobileNav = document.getElementById('mobileNav');

  function closeMobileMenu(restoreFocus = true) {
    if (!mobileNav || !mobileNav.classList.contains('active')) return;
    setPanelOpen(mobileNav, false);
    mobileToggle?.classList.remove('active');
    mobileToggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) mobileToggle?.focus({ preventScroll: true });
  }

  if (mobileToggle && mobileNav) {
    mobileToggle.addEventListener('click', () => {
      const open = !mobileNav.classList.contains('active');
      if (!open) { closeMobileMenu(false); return; }
      closePanel(false);
      setPanelOpen(mobileNav, true);
      mobileToggle.classList.add('active');
      mobileToggle.setAttribute('aria-expanded', 'true');
    });

    mobileNav.querySelectorAll('.mobile-nav__toggle').forEach(btn => {
      btn.addEventListener('click', event => {
        event.preventDefault();
        const sublist = btn.nextElementSibling;
        const open = !btn.classList.contains('active');
        if (sublist) sublist.classList.toggle('active', open);
        btn.classList.toggle('active', open);
        btn.setAttribute('aria-expanded', String(open));
      });
    });

    document.addEventListener('click', event => {
      if (!mobileNav.classList.contains('active')) return;
      if (mobileNav.contains(event.target) || mobileToggle.contains(event.target)) return;
      closeMobileMenu(false);
    });
  }

  /* --- Cart Drawer --- */
  const cartToggle = document.getElementById('cartToggle');
  const cartDrawer = document.getElementById('cartDrawer');
  const cartDrawerClose = document.getElementById('cartDrawerClose');

  function openCartDrawer(event) {
    openPanel(cartDrawer, event?.currentTarget instanceof Element ? event.currentTarget : document.activeElement, cartDrawerClose);
  }
  function closeCartDrawer() {
    if (activePanel?.panel === cartDrawer) closePanel();
  }

  if (cartToggle) cartToggle.addEventListener('click', openCartDrawer);
  if (cartDrawerClose) cartDrawerClose.addEventListener('click', closeCartDrawer);
  if (overlay) overlay.addEventListener('click', () => closePanel());

  // Cart drawer quantity + remove (one request at a time)
  let cartUpdating = false;
  document.addEventListener('click', event => {
    const qtyButton = event.target.closest('.cart-drawer__item-qty .qty-btn');
    const removeButton = event.target.closest('.cart-drawer__item-remove');
    if (!qtyButton && !removeButton) return;
    if (removeButton) { updateCart(removeButton.dataset.line, 0); return; }
    const span = qtyButton.parentElement.querySelector('span');
    let qty = parseInt(span.textContent, 10) || 0;
    if (qtyButton.dataset.action === 'increase') qty++;
    if (qtyButton.dataset.action === 'decrease') qty = Math.max(0, qty - 1);
    updateCart(qtyButton.dataset.line, qty);
  });

  function updateCart(line, quantity) {
    if (cartUpdating) return;
    cartUpdating = true;
    cartDrawer?.setAttribute('aria-busy', 'true');
    fetch(shopRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
    })
    .then(res => { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
    .then(() => { location.reload(); })
    .catch(err => {
      console.error('Cart update error:', err);
      cartUpdating = false;
      cartDrawer?.removeAttribute('aria-busy');
    });
  }

  /* --- Search Modal --- */
  const searchToggle = document.getElementById('searchToggle');
  const searchModal = document.getElementById('searchModal');
  const searchModalClose = document.getElementById('searchModalClose');

  function openSearchModal(event) {
    if (!searchModal) return;
    openPanel(searchModal, event?.currentTarget instanceof Element ? event.currentTarget : document.activeElement, searchModal.querySelector('input'));
  }

  if (searchToggle) searchToggle.addEventListener('click', openSearchModal);
  if (searchModalClose) searchModalClose.addEventListener('click', () => closePanel());
  if (searchModal) searchModal.addEventListener('click', event => { if (event.target === searchModal) closePanel(); });

  document.addEventListener('keydown', event => {
    trapFocus(event);
    if (event.key === 'Escape') {
      if (activePanel) closePanel();
      else closeMobileMenu();
    }
  });

  /* --- Product Page ---
     The sticky bar is position: fixed. Nothing here (or in CSS) may add transform, filter,
     perspective, contain or will-change to any of its ancestors. Its own show/hide transform
     is on the fixed element itself, which is safe. */
  const productControllers = new Map();

  function initProduct(section) {
    if (productControllers.has(section)) return;
    const form = section.querySelector('[data-product-form]');
    const mainButton = section.querySelector('#addToCart');
    const variantInput = section.querySelector('[name="id"]');
    const variantsTag = section.querySelector('#productJSON');
    if (!form || !mainButton || !variantInput || !variantsTag) return;
    const variants = JSON.parse(variantsTag.textContent);
    const prices = JSON.parse(section.querySelector('[data-variant-prices]')?.textContent || '{}');
    const priceElement = section.querySelector('#productPrice');
    const quantity = form.querySelector('[name="quantity"]');
    const stickyBar = section.querySelector('#stickyATC');
    const stickyButton = section.querySelector('#stickyATCBtn');
    const stickyPrice = section.querySelector('#stickyPrice');
    const stickyVariant = section.querySelector('#stickyVariant');
    const errorElement = form.querySelector('[data-product-error]');
    const statusElement = form.querySelector('[data-product-status]');
    const optionGroups = Array.from(form.querySelectorAll('.product__option'));
    const singleDefault = variants.length === 1 && /^default title$/i.test(String(variants[0]?.title || ''));
    const nav = document.getElementById('mobileBottomNav');
    const navAriaHidden = nav?.getAttribute('aria-hidden');
    const navInert = nav?.hasAttribute('inert');
    const abort = new AbortController();
    const listen = (target, event, callback, options = {}) => target?.addEventListener(event, callback, { ...options, signal: abort.signal });
    let currentVariant = variants.find(variant => String(variant.id) === variantInput.value);
    let pending = false;
    let disposed = false;

    function syncButtons() {
      const available = Boolean(currentVariant?.available);
      const text = pending && mainButton.dataset.addingText ? mainButton.dataset.addingText : currentVariant ? (available ? mainButton.dataset.addText : mainButton.dataset.soldText) : mainButton.dataset.unavailableText;
      [mainButton, stickyButton].forEach(button => {
        if (!button) return;
        button.disabled = pending || !available;
        button.textContent = text;
        button.setAttribute('aria-busy', String(pending));
        button.classList.toggle('is-sold-out', !pending && !available);
      });
      form.setAttribute('aria-busy', String(pending));
    }

    function moneyText(value) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = String(value).replace(/</g, '&lt;');
      return decoder.value;
    }

    // Mark option values that cannot form an available variant with the other current choices.
    function syncOptionStates() {
      if (!optionGroups.length) return;
      const selected = optionGroups.map(group => group.querySelector('input:checked')?.value);
      optionGroups.forEach((group, index) => {
        const label = group.querySelector('[data-option-selected]');
        if (label) label.textContent = selected[index] || '';
        group.querySelectorAll('.product__option-value').forEach(option => {
          const input = option.querySelector('input');
          if (!input) return;
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = variants.find(variant => variant.options.length === candidate.length && candidate.every((value, position) => value === variant.options[position]));
          option.classList.toggle('active', input.checked);
          option.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    function syncVariant() {
      variantInput.value = currentVariant ? currentVariant.id : '';
      const formatted = currentVariant && prices[currentVariant.id];
      if (quantity) {
        if (formatted?.quantityMax != null) quantity.max = formatted.quantityMax;
        else quantity.removeAttribute('max');
      }
      const price = currentVariant ? moneyText(formatted?.price || formatMoney(currentVariant.price)) : '';
      if (stickyPrice) stickyPrice.textContent = price;
      if (stickyVariant) stickyVariant.textContent = currentVariant && !singleDefault ? currentVariant.title : '';
      if (priceElement) {
        priceElement.replaceChildren();
        if (currentVariant) {
          const onSale = currentVariant.compare_at_price > currentVariant.price;
          const amount = document.createElement('span');
          amount.className = onSale ? 'price price--sale' : 'price';
          amount.textContent = price;
          priceElement.append(amount);
          if (onSale) {
            const compare = document.createElement('span');
            compare.className = 'price price--compare';
            compare.textContent = moneyText(formatted?.compare || formatMoney(currentVariant.compare_at_price));
            priceElement.append(' ', compare);
          }
        }
      }
      syncOptionStates();
      syncButtons();
    }

    // Swap the main image with a short opacity fade on the <img> only.
    function showImage(url, alt, imageId) {
      const image = section.querySelector('#productImage');
      if (!image || !url) return;
      section.querySelectorAll('.product__thumbnail').forEach(item => {
        const active = imageId != null ? item.dataset.imageId === String(imageId) : item.dataset.imageUrl === url;
        item.classList.toggle('active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      if (image.getAttribute('src') === url) return;
      if (alt != null) image.alt = alt;
      if (reducedMotion.matches) { image.src = url; return; }
      image.classList.add('is-swapping');
      const done = () => image.classList.remove('is-swapping');
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
      setTimeout(done, 900);
      image.src = url;
    }

    function variantImage(variant) {
      const media = variant?.featured_image;
      if (!media) return null;
      const thumb = section.querySelector(`.product__thumbnail[data-image-id="${CSS.escape(String(media.id))}"]`);
      if (thumb) return { url: thumb.dataset.imageUrl, alt: thumb.dataset.imageAlt, id: media.id };
      try {
        const url = new URL(media.src, location.href);
        url.searchParams.set('width', '1000');
        return { url: url.toString(), alt: media.alt || null, id: media.id };
      } catch { return { url: media.src, alt: media.alt || null, id: media.id }; }
    }

    listen(section, 'change', event => {
      if (!event.target.matches('.product__option-value input')) return;
      const selected = optionGroups.map(group => group.querySelector('input:checked')?.value);
      currentVariant = variants.find(variant => variant.options.length === selected.length && selected.every((value, index) => value === variant.options[index]));
      syncVariant();
      if (errorElement) errorElement.hidden = true;
      const url = new URL(window.location.href);
      if (currentVariant) url.searchParams.set('variant', currentVariant.id);
      else url.searchParams.delete('variant');
      history.replaceState(history.state, '', url);
      const next = variantImage(currentVariant);
      if (next) showImage(next.url, next.alt, next.id);
    });

    listen(section, 'click', event => {
      const thumbnail = event.target.closest('.product__thumbnail');
      if (!thumbnail) return;
      showImage(thumbnail.dataset.imageUrl, thumbnail.dataset.imageAlt || '', thumbnail.dataset.imageId);
    });

    listen(stickyButton, 'click', () => {
      if (pending || mainButton.disabled || !currentVariant?.available || variantInput.value !== String(currentVariant.id)) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
      else mainButton.click();
    });

    listen(form, 'submit', async event => {
      event.preventDefault();
      if (pending || mainButton.disabled || !currentVariant?.available || variantInput.value !== String(currentVariant.id)) return;
      if (!form.reportValidity()) return;
      const body = new FormData(form);
      const root = (window.Shopify?.routes?.root || section.dataset.rootUrl || '/').replace(/\/?$/, '/');
      pending = true;
      syncButtons();
      if (errorElement) errorElement.hidden = true;
      try {
        const response = await fetch(root + 'cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
        const item = await response.json();
        if (!response.ok) throw new Error(item.description || item.message || 'HTTP ' + response.status);
      } catch (error) {
        pending = false;
        if (!disposed) {
          syncButtons();
          if (errorElement) {
            errorElement.textContent = error.message;
            errorElement.hidden = false;
            errorElement.scrollIntoView({ block: 'nearest' });
          }
        }
        return;
      }
      if (disposed) return;
      if (statusElement && mainButton.dataset.addedText) statusElement.textContent = mainButton.dataset.addedText;
      openCartDrawer();
      try {
        const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const cart = await response.json();
          ['cartCount', 'mobileCartCount'].forEach(id => {
            const countElement = document.getElementById(id);
            if (!countElement) return;
            countElement.textContent = cart.item_count;
            countElement.classList.toggle('hidden', cart.item_count === 0);
          });
        }
      } catch (error) {
        console.error('Cart refresh error:', error);
      }
      if (!disposed) location.reload();
    });

    const media = window.matchMedia('(max-width: 768px)');
    const viewport = window.visualViewport;
    let mainBottom = Infinity;
    let boundaryTop = Infinity;
    let frame = 0;
    let needsMeasure = true;
    let visible = false;
    let boundaries = [];

    function setVisible(next) {
      if (!stickyBar || visible === next) return;
      visible = next;
      stickyBar.classList.toggle('is-visible', next);
      stickyBar.setAttribute('aria-hidden', String(!next));
      stickyBar.toggleAttribute('inert', !next);
      if (nav) {
        nav.toggleAttribute('data-sticky-atc-active', next);
        nav.toggleAttribute('inert', next || navInert);
        if (next) nav.setAttribute('aria-hidden', 'true');
        else {
          if (navAriaHidden === null) nav.removeAttribute('aria-hidden');
          else nav.setAttribute('aria-hidden', navAriaHidden);
          nav.style.transform = 'translateY(0)';
        }
      }
    }

    function updateSticky() {
      frame = 0;
      if (disposed || !stickyBar) return;
      if (needsMeasure) {
        mainBottom = mainButton.getBoundingClientRect().bottom + window.scrollY;
        boundaries = Array.from(document.querySelectorAll('.complete-look, .k3sf-product-entry, footer')).filter(element => section.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
        boundaryTop = Math.min(...boundaries.map(element => element.getBoundingClientRect().top + window.scrollY));
        needsMeasure = false;
      }
      const top = Math.max(0, window.scrollY + (viewport?.offsetTop || 0));
      const height = viewport?.height || window.innerHeight;
      const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      const keyboardOpen = editing && height < window.innerHeight * 0.8;
      const blocked = document.body.style.overflow === 'hidden' || document.querySelector('#mobileNav.active, #cartDrawer.active, #searchModal.active, #overlay.active, dialog[open], [aria-modal="true"]:not(dialog):not([hidden]):not([aria-hidden="true"])');
      const passedMain = top >= mainBottom + (visible ? 0 : 8);
      const beforeBoundary = top + height < boundaryTop - (visible ? 0 : 8);
      setVisible(media.matches && passedMain && beforeBoundary && !blocked && !keyboardOpen && (!viewport || viewport.scale <= 1.05));
    }

    function schedule(measure = false) {
      if (disposed) return;
      needsMeasure = needsMeasure || measure;
      if (!frame) frame = requestAnimationFrame(updateSticky);
    }
    const geometryChanged = () => schedule(true);
    const positionChanged = () => schedule();
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(geometryChanged) : null;
    [document.body, section, mainButton].forEach(element => resizeObserver?.observe(element));
    const intersectionObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(geometryChanged) : null;
    intersectionObserver?.observe(mainButton);
    const overlayObserver = new MutationObserver(records => {
      if (records.some(record => record.target === document.body || record.target.matches?.('#mobileNav, #cartDrawer, #searchModal, #overlay, dialog, [aria-modal="true"]'))) positionChanged();
    });
    overlayObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-modal', 'aria-hidden'] });
    listen(window, 'scroll', positionChanged, { passive: true });
    listen(window, 'resize', geometryChanged);
    listen(window, 'orientationchange', geometryChanged);
    listen(window, 'pageshow', geometryChanged);
    listen(viewport, 'resize', geometryChanged);
    listen(viewport, 'scroll', positionChanged, { passive: true });
    listen(media, 'change', geometryChanged);
    listen(document, 'load', geometryChanged, { capture: true });
    listen(document.fonts, 'loadingdone', geometryChanged);
    document.fonts?.ready.then(geometryChanged);
    listen(document, 'focusin', positionChanged);
    listen(document, 'focusout', positionChanged);
    listen(document, 'shopify:section:load', geometryChanged);
    listen(document, 'shopify:section:unload', geometryChanged);
    if (stickyBar) {
      stickyBar.setAttribute('aria-hidden', 'true');
      stickyBar.setAttribute('inert', '');
    }
    syncVariant();
    schedule(true);
    productControllers.set(section, () => {
      disposed = true;
      abort.abort();
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      overlayObserver.disconnect();
      setVisible(false);
      productControllers.delete(section);
    });
  }

  function initProducts(root) {
    if (root.matches?.('[data-product-section]')) initProduct(root);
    root.querySelectorAll('[data-product-section]').forEach(initProduct);
  }
  initProducts(document);
  document.addEventListener('shopify:section:load', event => initProducts(event.target));
  document.addEventListener('shopify:section:unload', event => {
    productControllers.forEach((cleanup, section) => {
      if (event.target === section || event.target.contains(section)) cleanup();
    });
  });

  // Quantity selector (product page + cart page)
  document.addEventListener('click', event => {
    const button = event.target.closest('.quantity-selector .qty-btn');
    if (!button) return;
    const input = button.closest('.quantity-selector').querySelector('input');
    if (!input) return;
    const value = Number(input.value) || 1;
    const minimum = input.min === '' ? 1 : Number(input.min);
    const maximum = input.max === '' ? Infinity : Number(input.max);
    const step = Number(input.step) || 1;
    if (button.dataset.action === 'decrease' && value > minimum) input.value = Math.max(minimum, value - step);
    if (button.dataset.action === 'increase' && value < maximum) input.value = Math.min(maximum, value + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --- Collection Sort --- */
  const sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function() {
      const url = new URL(window.location);
      url.searchParams.set('sort_by', this.value);
      url.searchParams.delete('page');
      window.location = url.toString();
    });
  }

  /* --- Customer: Login/Recover toggle --- */
  const recoverLink = document.getElementById('recoverLink');
  const cancelRecover = document.getElementById('cancelRecover');
  const recoverForm = document.getElementById('recoverForm');

  if (recoverLink && recoverForm) {
    recoverLink.addEventListener('click', function(e) {
      e.preventDefault();
      recoverForm.style.display = 'block';
      recoverLink.closest('form').style.display = 'none';
    });
  }
  if (cancelRecover && recoverForm) {
    cancelRecover.addEventListener('click', function(e) {
      e.preventDefault();
      recoverForm.style.display = 'none';
      document.querySelector('[data-login-form], form[action*="login"]').style.display = 'block';
    });
  }

  /* --- Mobile Bottom Nav --- */
  const mobileSearchBtn = document.getElementById('mobileSearchBtn');
  const mobileCartBtn = document.getElementById('mobileCartBtn');
  if (mobileSearchBtn) mobileSearchBtn.addEventListener('click', openSearchModal);
  if (mobileCartBtn) mobileCartBtn.addEventListener('click', openCartDrawer);

  /* --- Hide/Show Bottom Nav on Scroll (writes only when the state changes) --- */
  const bottomNav = document.getElementById('mobileBottomNav');
  if (bottomNav) {
    let lastScrollY = window.scrollY;
    let ticking = false;
    let hidden = false;
    window.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function() {
        ticking = false;
        const currentScroll = window.scrollY;
        const delta = currentScroll - lastScrollY;
        if (Math.abs(delta) < 6) return;
        const next = delta > 0 && currentScroll > 200;
        lastScrollY = currentScroll;
        if (next === hidden && bottomNav.style.transform) return;
        hidden = next;
        bottomNav.style.transform = next ? 'translateY(100%)' : 'translateY(0)';
      });
    }, { passive: true });
  }

  /* --- Hero background video: pause control, reduced motion, off-screen pause --- */
  document.querySelectorAll('[data-hero-video]').forEach(video => {
    const toggle = video.parentElement.querySelector('[data-hero-video-toggle]');
    let userPaused = false;
    const setState = playing => {
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(!playing));
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      toggle.classList.toggle('is-paused', !playing);
    };
    const tryPlay = () => { const p = video.play(); if (p && p.catch) p.catch(() => setState(false)); };
    if (reducedMotion.matches) { video.removeAttribute('autoplay'); video.pause(); userPaused = true; setState(false); }
    video.addEventListener('play', () => setState(true));
    video.addEventListener('pause', () => setState(false));
    toggle?.addEventListener('click', () => {
      if (video.paused) { userPaused = false; tryPlay(); } else { userPaused = true; video.pause(); }
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) { if (!video.paused) video.pause(); }
        else if (!userPaused) tryPlay();
      }, { threshold: 0.15 }).observe(video);
    }
  });

  /* --- Product carousels: arrow buttons on top of native scrolling --- */
  document.querySelectorAll('[data-carousel]').forEach(carousel => {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      const max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
      carousel.toggleAttribute('data-carousel-static', max <= 0);
    };
    const step = direction => {
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const unit = card ? card.getBoundingClientRect().width + gap : 0;
      const amount = unit ? unit * Math.max(1, Math.floor((track.clientWidth + gap + 1) / unit)) : track.clientWidth;
      track.scrollBy({ left: direction * amount, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(sync); }, { passive: true });
    if (typeof ResizeObserver === 'function') new ResizeObserver(sync).observe(track);
    sync();
  });

  /* --- Reveal on scroll ---
     Optional and one-time. Only elements below the fold at load are hidden, so nothing that is
     already on screen flashes. Product pages use opacity only, so sticky-bar geometry never moves. */
  function initReveal() {
    if (!('IntersectionObserver' in window) || reducedMotion.matches) return;
    const selectors = '.section-header, .product-card, .collection-list__item, .image-text__image, .image-text__content, .brand-values__item, .testimonial, .blog__card, .k3sf-entry__inner, .complete-look__panel, .k3sf-product-entry__inner, .instagram-feed__item, .rich-text__inner, .promo-panel, .editorial-card';
    const stickyBar = document.getElementById('stickyATC');
    const fold = window.innerHeight * 0.92;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
    let index = 0;
    document.querySelectorAll(selectors).forEach(element => {
      if (element.closest('[data-product-section], .cart-drawer, .search-modal, .mobile-nav, kara3-style-finder')) return;
      if (stickyBar && element.contains(stickyBar)) return;
      // Items inside horizontal swipe rows stay visible (no fade while swiping).
      const parentScroll = element.parentElement && getComputedStyle(element.parentElement).overflowX;
      if (parentScroll === 'auto' || parentScroll === 'scroll') return;
      if (element.getBoundingClientRect().top < fold) return;
      element.classList.add('k3-reveal');
      element.style.setProperty('--reveal-delay', (index++ % 4) * 60 + 'ms');
      observer.observe(element);
    });
    // Safety net: never leave content hidden if the observer misses something.
    window.addEventListener('beforeprint', () => document.querySelectorAll('.k3-reveal').forEach(el => el.classList.add('is-revealed')));
  }
  initReveal();

  /* --- Utility: Format Money --- */
  function formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    });
  }

})();
